import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiArrowLeft, FiUser, FiClock, FiActivity } from 'react-icons/fi';
import { logAPI } from '../services/api';
import LogTable from '../components/LogTable';
import { format } from 'date-fns';
import '../styles/PlayerLogs.css';

const PlayerLogs = () => {
    const { steamId } = useParams();
    const navigate = useNavigate();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 50,
        total: 0,
        pages: 0
    });
    const [playerInfo, setPlayerInfo] = useState(null);

    useEffect(() => {
        if (steamId) {
            fetchPlayerLogs(1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [steamId]);

    const fetchPlayerLogs = async (page = 1) => {
        try {
            setLoading(true);
            const response = await logAPI.getPlayerLogs(steamId, { page, limit: 50 });
            setLogs(response.data.logs);
            setPagination(response.data.pagination);

            // Extract player info from first log
            if (response.data.logs.length > 0) {
                const firstLog = response.data.logs[0];
                setPlayerInfo({
                    name: firstLog.playerName || 'Unknown',
                    steamId: firstLog.playerSteamId,
                    firstSeen: response.data.logs[response.data.logs.length - 1]?.timestamp,
                    lastSeen: firstLog.timestamp,
                    totalLogs: response.data.pagination.total
                });
            }
        } catch (error) {
            console.error('Error fetching player logs:', error);
            toast.error('Failed to fetch player logs');
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (newPage) => {
        fetchPlayerLogs(newPage);
    };

    const getActionStats = () => {
        const stats = {};
        logs.forEach(log => {
            const type = log.actionType || 'other';
            stats[type] = (stats[type] || 0) + 1;
        });
        return Object.entries(stats)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 5);
    };

    return (
        <div className="player-logs-page">
            <button className="back-btn" onClick={() => navigate(-1)}>
                <FiArrowLeft />
                <span>Back</span>
            </button>

            <div className="player-header">
                <div className="player-avatar">
                    <FiUser />
                </div>
                <div className="player-details">
                    <h1>{playerInfo?.name || 'Loading...'}</h1>
                    <p className="steam-id">{decodeURIComponent(steamId)}</p>
                </div>
            </div>

            {playerInfo && (
                <div className="player-stats">
                    <div className="stat-item">
                        <FiActivity className="stat-icon" />
                        <div className="stat-content">
                            <span className="stat-value">{playerInfo.totalLogs}</span>
                            <span className="stat-label">Total Logs</span>
                        </div>
                    </div>
                    <div className="stat-item">
                        <FiClock className="stat-icon" />
                        <div className="stat-content">
                            <span className="stat-value">
                                {playerInfo.firstSeen 
                                    ? format(new Date(playerInfo.firstSeen), 'dd/MM/yyyy')
                                    : 'N/A'
                                }
                            </span>
                            <span className="stat-label">First Seen</span>
                        </div>
                    </div>
                    <div className="stat-item">
                        <FiClock className="stat-icon" />
                        <div className="stat-content">
                            <span className="stat-value">
                                {playerInfo.lastSeen 
                                    ? format(new Date(playerInfo.lastSeen), 'dd/MM/yyyy HH:mm')
                                    : 'N/A'
                                }
                            </span>
                            <span className="stat-label">Last Seen</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Action breakdown */}
            {logs.length > 0 && (
                <div className="action-breakdown">
                    <h3>Recent Action Types</h3>
                    <div className="action-tags">
                        {getActionStats().map(([action, count]) => (
                            <span key={action} className="action-tag">
                                {action.replace(/_/g, ' ')}: {count}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            <div className="player-logs-section">
                <h2>Activity Log</h2>
                <LogTable 
                    logs={logs}
                    loading={loading}
                    pagination={pagination}
                    onPageChange={handlePageChange}
                />
            </div>
        </div>
    );
};

export default PlayerLogs;
